import { gql } from '@apollo/client';
import { toast } from 'react-semantic-toasts';
import { captureException } from '@playbookux/playbook-web-core';

import client from './graphql';

export const getProjects = gql`
  query Projects(
    $companyId: ID!,
    $name: String,
    $description: String,
    $status: ProjectStatusEnum,
    $folderIds: [ID!],
    $projectCreatorsIds: [ID!],
    $order: String,
    $limit: Int!,
    $offset: Int!) {
    projects(
        companyId: $companyId,
        name: $name,
        description: $description,
        status: $status,
        folderIds: $folderIds,
        projectCreatorsIds: $projectCreatorsIds,
        order: $order,
        limit: $limit,
        offset: $offset
      ) {
      id
      name
      testType
      participantsSource
      status
      description
      testType
      createdAt
      launchedOn
      deletedAt
      status
      shareEnabled
      shareCode
      sharePassword
      sortType
      randomCategory
      sortRandom
      tooltipDescription
      uploadImage
      hideName
      sessionDuration
      draftDevices
      rejectedBy {
        id
        user {
          profile {
            firstName
            lastName
          }
        }
      }
      requestedReviewers {
        id
        user {
          profile {
            firstName
            lastName
          }
        }
      }
      segments {
        id
        name
        audienceParticipants
        device
        operatingSystem
        countApprovedTesters
        receivedTests
        countScheduledTesters
      }
      folder {
        id
      }
      company {
        id
      }
      createdBy {
        id
        profile {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const getIsCompanyMemberByCompanyId = async (companyId) => {
  const query = gql`
    query isCompanyMember($companyId: ID!) {
      isCompanyMember(companyId: $companyId) {
        isCompanyMember
      }
    }
  `;

  const { data, error } = await client.getClient().query({
    query,
    variables: { companyId },
  });

  const { isCompanyMember } = data;

  return { isCompanyMember, error };
};

export const getProjectById = async (id) => {
  const query = gql`
    query Project($id: ID!) {
      project(id: $id) {
        id
        name
        description
        testType
        status
        sessionDuration
        ndaRequired
        studyContext
        otherTesterRequirements
        participantsSource
        lockVersion
        sortType
        randomCategory
        sortRandom
        tooltipDescription
        uploadImage
        hideName
        instructionsContent
        welcomeContent
        thankYouContent
        instructionsTitle
        welcomeTitle
        thankYouTitle
        cardSortItems {
          id
          name
          description
          itemOrder
          imageKey
          imageUrl
        }
        cardSortCategories {
          id
          name
          isDefault
          categoryOrder
        }
        questionTemplate {
          id
          name
        }
        company {
          id
        }
        surveys(surveyType: "session") {
          id
          surveyType
          questions {
            id
            label
            questionType
            groupId
            questionOrder
            leftLabel
            rightLabel
            blurVideo
            treeTestNodes {
              id
            }
            followUpQuestions{
              id
              label
              questionType
              leftLabel
              rightLabel
              questionOrder
              questionOptions {
                id
                label
                disqualify
                qualify
              }
            }
            questionOptions {
              id
              label
              disqualify
              qualify
              optionType
              order
            }
          }
          surveysPages {
            id
            pageName
            order
            skipLogic {
              conditions {
                id
                surveyPageId
                skipConditions {
                  id
                  questionId
                  questionOptionId
                  operation
                }
              }
            }
            questions {
              id
              label
              questionType
              groupId
              questionOrder
              leftLabel
              rightLabel
              blurVideo
              questionOptions {
                id
                label
                disqualify
                qualify
                optionType
                order
              }
            }
          }
        }
        treeTestNodes {
          id
          label
          parentId
          order
          projectId
        }
        segments {
          id
          name
          device
          createdAt
          audienceParticipants
          audienceTemplate {
            id
            name
          }
          participantsLanguage
          operatingSystem
          projectId
          surveys(surveyType: "screener") {
            id
            surveyType
            questions {
              id
              label
              questionType
              groupId
              questionOrder
              leftLabel
              rightLabel
              blurVideo
              treeTestNodes {
                id
              }
              followUpQuestions{
                id
                label
                questionType
                leftLabel
                rightLabel
                questionOrder
                questionOptions {
                  id
                  label
                  disqualify
                  qualify
                }
              }
              questionOptions {
                id
                label
                disqualify
                qualify
                optionType
                order
              }
            }
          }
          audienceAttributes {
            id
            audienceAttributeType
            audienceAttributeOptions {
              id
              value
              name
            }
          }
        }
      }
    }
  `;

  const { data } = await client.getClient().query({
    query,
    variables: { id },
  });

  return data;
};

export const getCompanyMember = async (companyId) => {
  const query = gql`
    query CompanyMember($companyId: ID) {
      companyMember(companyId: $companyId) {
        company {
          id
          balanceRemaining
          name
          plan
          balanceUsed
          stripeCustomerId
          inviteLink
          inviteToggleUserId
          consentFormRequired
          collectByoTestersInfo
        }
      }
    }
  `;

  const { data } = await client.getClient().query({
    fetchPolicy: 'network-only',
    query,
    variables: { companyId },
  });

  return data?.companyMember?.company;
};

export const updateStudyMutation = async (id, attributes) => {
  const query = gql`
    mutation UpdateStudy($id: ID!, $attributes: ProjectAttributes!) {
      updateStudy(id: $id, attributes: $attributes) {
        project {
          id
          name
          description
          surveys(surveyType: "session") {
            id
            surveyType
            questions {
              id
              label
              questionType
              questionOrder
              leftLabel
              rightLabel
              blurVideo
              randomize
              questionOptions {
                id
                label
                disqualify
                qualify
                optionType
                order
              }
            }
            surveysPages {
              id
              pageName
              order
              skipLogic {
                conditions {
                  id
                  surveyPageId
                  skipConditions {
                    id
                    questionId
                    questionOptionId
                    operation
                  }
                }
              }
              questions {
                id
                label
                questionType
                questionOrder
                leftLabel
                rightLabel
                blurVideo
                questionOptions {
                  id
                  label
                  disqualify
                  qualify
                  optionType
                  order
                }
              }
            }
          }
        }
      }
    }
  `;

  const { data } = await client.getClient().mutate({
    mutation: query,
    variables: { id, attributes },
  });

  return data;
};

export const getCompanyById = async (id) => {
  const query = gql`
    query Company($id: ID!) {
      company(id: $id) {
        id
        demoProjectVisibility
        plan
      }
    }
  `;

  const { data } = await client.getClient().query({
    query,
    variables: { id },
  });

  return data;
};

export const duplicateProject = async (projectId, queryAttributes) => {
  const query = gql`
    mutation DuplicateProject(
      $projectId: ID!,
    ) {
      duplicateProject(
        projectId: $projectId,
      ) {
        project {
          name
          id
          name
          testType
          participantsSource
          status
          description
          testType
          createdAt
          launchedOn
          deletedAt
          status
          shareEnabled
          shareCode
          sharePassword
          sortType
          randomCategory
          sortRandom
          tooltipDescription
          uploadImage
          hideName
          sessionDuration
          segments {
            id
            name
            audienceParticipants
            device
            operatingSystem
            countApprovedTesters
            receivedTests
            countScheduledTesters
          }
          folder {
            id
          }
          company {
            id
          }
          createdBy {
            id
            profile {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  `;

  const { data, errors } = await client.getClient().mutate({
    mutation: query,
    variables: {
      projectId,
    },
  });

  if (!errors) {
    let projectName = data.duplicateProject.project.name;
    projectName = projectName.substring(0, projectName.length - 5);
    projectName = projectName.length > 25
      ? `${projectName.substring(0, 25)}...` : projectName;
    toast({
      description: `${projectName} has been duplicated`,
      time: 5000,
    });
  }

  let tempData;
  try {
    tempData = client.getClient().readQuery({
      query: getProjects,
      variables: { ...queryAttributes.variables },
    });
  } catch (e) {
    captureException('HANDLE_DUPLICATE_PROJECT_READ_QUERY_FAILED', e);
  }

  try {
    client.getClient().writeQuery({
      query: getProjects,
      data: {
        projects: [
          { ...data.duplicateProject.project },
          ...tempData.projects,
        ],
      },
      variables: { ...queryAttributes.variables },
    });
  } catch (e) {
    captureException('HANDLE_DUPLICATE_PROJECT_WRITE_QUERY_FAILED', e);
  }

  return data;
};
